import { Draggable, Droppable } from '@hello-pangea/dnd';
import { ChevronDown, ExternalLink, GripVertical } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { DynamicDeliveryTag } from 'components/DynamicDeliveryTag';
import { FreefoodIcon } from 'components/FreefoodIcon';
import { DeliveryState, TaskState, TourState } from 'data/__generated__';
import { UNASSIGNED_COLOR } from 'pages/DynamicDeliveryPlanning/constants/approColors';

import {
    boxCounts,
    failedDeliveryCount,
    formatWindow,
    deliveryFullLabel,
    deliveryMetaLabel,
    deliveryWindow,
    getStopState,
    hasFreefood,
    isTask,
    orderedStops,
    stopCountLabel,
    stopWindow,
    TASK_STATE_LABELS,
    taskLabel,
    tourOrigin,
    type Tour,
    type TourDelivery,
    type TourTask,
} from '../helpers';
import { ApproPicker, type ApproOption } from './ApproPicker';
import { OriginIcon } from './OriginIcon';
import { STATE_TONE, StatePill, TASK_STATE_TONE, WindowChip } from './StopStatus';
import { TaskIcon } from './TaskStop';
import { DELIVERY_STATES_LABELS } from 'pages/Deliveries/constants/states';

export const tourDroppableId = (tourId: string): string => `tour:${tourId}`;
export const parseTourDroppableId = (droppableId: string): string | null =>
    droppableId.startsWith('tour:') ? droppableId.slice('tour:'.length) : null;
/** Droppable id of the « À affecter » panel (LoosePanel) — parseTourDroppableId returns null for it. */
export const LOOSE_DROPPABLE_ID = 'a-affecter';

type Props = {
    tour: Tour;
    deliveryPersonOptions: ApproOption[];
    /** True while a delivery card is dragged — a collapsed tour then shows a drop strip. */
    isDragActive: boolean;
    /** Assigns the WHOLE tour to an approvisionneur (the dispatcher's main action here). */
    onAssignDeliveryPerson: (deliveryPersonUserId: string | null) => void;
    /** When set, each delivery shows a link to its delivery detail page. */
    stopHref?: (deliveryId: string) => string;
    /** « Éditer »: clicking a card (de)selects the delivery; a selected card shows its click-order number. */
    selectionNumberByDeliveryId?: Map<string, number>;
    onToggleDelivery?: (deliveryId: string) => void;
    onOpenTask?: (task: TourTask) => void;
    focusedDeliveryId?: string | null;
};

export const TaskStopContent = ({ task }: { task: TourTask }) => {
    const { start, stop } = stopWindow(task);
    return (
        <>
            <StopBody title={`${taskLabel(task)}\n${task.description}`}>
                <StopTitle>
                    <SiteName>{taskLabel(task)}</SiteName>
                    <TaskIcon size={13} />
                </StopTitle>
                <StopMeta>{task.description}</StopMeta>
            </StopBody>
            <StopEnd>
                <WindowChip $muted={!start && !stop}>{formatWindow(start, stop)}</WindowChip>
                <StatePill $tone={TASK_STATE_TONE[task.state]}>{TASK_STATE_LABELS[task.state]}</StatePill>
            </StopEnd>
        </>
    );
};

const DeliveryStopContent = ({
    delivery,
    stopHref,
}: {
    delivery: TourDelivery;
    stopHref?: (deliveryId: string) => string;
}) => (
    <>
        {/* Hover on the name or the address: full site name, exact address and columns count — what the two
            truncated lines may have cut. */}
        <StopBody title={`${deliveryFullLabel(delivery)}\n${deliveryMetaLabel(delivery)}`}>
            <StopTitle>
                <SiteName>{deliveryFullLabel(delivery)}</SiteName>
                {/* Per STOP, not per tour: a tour is often mixed, so its own icon says nothing about the delivery
                    you are reading. */}
                <DynamicDeliveryTag isDynamic={delivery.isDynamic} label="Livraison dynamique" size={13} />
                {hasFreefood(delivery) && <FreefoodIcon size={13} />}
            </StopTitle>
            {/* Address, zone and contract (when they exist), columns count on one muted line. */}
            <StopMeta>{deliveryMetaLabel(delivery)}</StopMeta>
        </StopBody>
        {/* Window and state on ONE line: the row is as tall as its two text lines. */}
        <StopEnd>
            <WindowChip $muted={!deliveryWindow(delivery).start && !deliveryWindow(delivery).stop}>
                {formatWindow(deliveryWindow(delivery).start, deliveryWindow(delivery).stop)}
            </WindowChip>
            <StatePill $tone={STATE_TONE[getStopState(delivery)] ?? 'neutral'}>
                {DELIVERY_STATES_LABELS[getStopState(delivery)]}
            </StatePill>
        </StopEnd>
        {stopHref && (
            <StopLink
                to={stopHref(delivery._id)}
                title="Ouvrir la fiche livraison"
                onClick={(event) => event.stopPropagation()}
            >
                <ExternalLink size={14} />
            </StopLink>
        )}
    </>
);

/**
 * One tour card: the dispatcher's working unit, whatever stream composed it. Assign an appro via the
 * searchable select, reorder its deliveries by drag, or drag a delivery onto another card to recompose the tours.
 */
export const RoundSection = ({
    tour,
    deliveryPersonOptions,
    isDragActive,
    onAssignDeliveryPerson,
    stopHref,
    selectionNumberByDeliveryId,
    onToggleDelivery,
    onOpenTask,
    focusedDeliveryId = null,
}: Props) => {
    const [collapsed, setCollapsed] = useState(false);
    const taskCount = tour.stops.filter(({ __typename }) => __typename === 'Task').length;
    const color = tour.color || UNASSIGNED_COLOR;
    const delivered = tour.deliveries.filter((delivery) => getStopState(delivery) === DeliveryState.Delivered).length;
    const finishedTasks = tour.stops.filter(
        (stop) => stop.__typename === 'Task' && stop.state === TaskState.Finished,
    ).length;
    // Columns to service, the unit ops sizes a tour with — the machines, not the sites.
    const columns = tour.deliveries.reduce(
        (total, delivery) => total + delivery.deliveredMicrostoreColumnIds.length,
        0,
    );
    const failed = failedDeliveryCount(tour);
    const boxes = boxCounts(tour);
    const isFinished = tour.state === TourState.Finished;

    return (
        <Section $unassigned={tour.deliveryPersonUserId === null} data-test="tour-round-section">
            <Head>
                {/* The badge IS the tour name — displayed once, renaming happens on the timeline. */}
                <RoundBadge style={{ backgroundColor: color }} title={tour.name}>
                    {tour.name}
                </RoundBadge>
                <OriginIcon origin={tourOrigin(tour)} />
                <HeadActions>
                    <ApproPicker
                        value={tour.deliveryPersonUserId ?? null}
                        onChange={onAssignDeliveryPerson}
                        options={deliveryPersonOptions}
                        dataTest="tour-round-delivery-person"
                    />
                    <CollapseButton
                        type="button"
                        onClick={() => setCollapsed((current) => !current)}
                        aria-label={collapsed ? 'Déplier' : 'Replier'}
                        $collapsed={collapsed}
                    >
                        <ChevronDown size={16} />
                    </CollapseButton>
                </HeadActions>
            </Head>

            {/* Own line: the appro picker eats the header's width, the figures were being clipped there. */}
            <Stats>
                {stopCountLabel(tour.deliveries.length, taskCount)} · <b>{columns}</b> colonne
                {columns > 1 ? 's' : ''} · <b>{delivered + finishedTasks}</b>/{tour.deliveries.length + taskCount}{' '}
                {taskCount ? 'faits' : 'livré'}
                {failed > 0 && (
                    <Failed>
                        {' · '}
                        {failed} non livrée{failed > 1 ? 's' : ''}
                    </Failed>
                )}
                {/* One boxes figure, the one that matters now: scanned out before the tour, checked back after. */}
                {boxes.total > 0 &&
                    (isFinished ? (
                        <BoxesFigure $alert={boxes.returned < boxes.total}>
                            {' · '}
                            <b>{boxes.returned}</b>/{boxes.total} caisse{boxes.total > 1 ? 's' : ''} rentrée
                            {boxes.total > 1 ? 's' : ''}
                        </BoxesFigure>
                    ) : (
                        <span>
                            {' · '}
                            <b>{boxes.loaded}</b>/{boxes.total} caisse{boxes.total > 1 ? 's' : ''} chargée
                            {boxes.total > 1 ? 's' : ''}
                        </span>
                    ))}
            </Stats>

            <Droppable droppableId={tourDroppableId(tour._id)}>
                {(dropProvided, dropSnapshot) => (
                    <DropZone
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        $over={dropSnapshot.isDraggingOver}
                        $collapsed={collapsed && !isDragActive}
                    >
                        {collapsed && isDragActive && (
                            <EmptyDropHint>Déposez ici pour déplacer dans {tour.name}</EmptyDropHint>
                        )}
                        {!collapsed &&
                            orderedStops(tour).map((stop, index) => (
                                <Draggable key={stop._id} draggableId={stop._id} index={index}>
                                    {(dragProvided, dragSnapshot) => (
                                        <DeliveryCard
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            {...dragProvided.dragHandleProps}
                                            $dragging={dragSnapshot.isDragging}
                                            $pending={selectionNumberByDeliveryId?.has(stop._id) ?? false}
                                            $focused={stop._id === focusedDeliveryId}
                                            data-test={isTask(stop) ? 'tour-round-task' : 'tour-round-delivery'}
                                            onClick={
                                                isTask(stop) && onOpenTask
                                                    ? () => onOpenTask(stop)
                                                    : onToggleDelivery && (() => onToggleDelivery(stop._id))
                                            }
                                        >
                                            <GripVertical size={14} />
                                            <OrderBadge style={{ backgroundColor: color }}>
                                                {selectionNumberByDeliveryId?.get(stop._id) ?? index + 1}
                                            </OrderBadge>
                                            {isTask(stop) ? (
                                                <TaskStopContent task={stop} />
                                            ) : (
                                                <DeliveryStopContent delivery={stop} stopHref={stopHref} />
                                            )}
                                        </DeliveryCard>
                                    )}
                                </Draggable>
                            ))}
                        {dropProvided.placeholder}
                    </DropZone>
                )}
            </Droppable>
        </Section>
    );
};

const StopLink = styled(Link)`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.infoTextColor};

    &:hover {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const Section = styled.div<{ $unassigned: boolean }>`
    background: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme, $unassigned }) => ($unassigned ? theme.warningColor : theme.lightBorderColor)};
    border-radius: 12px;
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
`;

const RoundBadge = styled.span`
    min-width: 36px;
    max-width: 130px;
    height: 26px;
    padding: 0 8px;
    border-radius: 8px;
    line-height: 26px;
    text-align: center;
    font-weight: 800;
    font-size: 12px;
    color: #fff;
    flex-shrink: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Stats = styled.div`
    padding: 0 12px 9px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    b {
        color: ${({ theme }) => theme.textColor};
    }
`;

const HeadActions = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

/** Failures live next to the delivered fraction, not in the status — see tourStateLabel. */
const BoxesFigure = styled.span<{ $alert: boolean }>`
    color: ${({ $alert, theme }) => ($alert ? theme.errorColor : 'inherit')};
`;

const Failed = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-weight: 700;
`;

const CollapseButton = styled.button<{ $collapsed: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 4px;
    cursor: pointer;
    color: ${({ theme }) => theme.infoTextColor};
    transition: transform 0.18s ease;
    transform: rotate(${({ $collapsed }) => ($collapsed ? '-90deg' : '0deg')});
    flex-shrink: 0;
`;

const DropZone = styled.div<{ $over: boolean; $collapsed: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-height: ${({ $collapsed }) => ($collapsed ? '0' : '48px')};
    margin: 0 6px 6px;
    padding: ${({ $over }) => ($over ? '6px' : '0')};
    border-radius: 10px;
    border: 1px dashed ${({ theme, $over }) => ($over ? theme.ctaPrimaryColor : 'transparent')};
    background: ${({ theme, $over }) => ($over ? theme.backgroundColor : 'transparent')};
    transition:
        background 0.15s ease,
        border-color 0.15s ease,
        padding 0.15s ease;
`;

const EmptyDropHint = styled.div`
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 8px;
    padding: 12px;
    text-align: center;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const DeliveryCard = styled.div<{ $dragging: boolean; $pending: boolean; $focused: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 4px;
    border-radius: 10px;
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid
        ${({ theme, $pending, $focused }) =>
            $pending ? '#f59e0b' : $focused ? theme.ctaPrimaryColor : theme.lightBorderColor};
    color: ${({ theme }) => theme.infoTextColor};
    cursor: grab;
    box-shadow: ${({ $dragging }) => ($dragging ? '0 12px 28px rgba(0, 0, 0, 0.3)' : 'none')};

    &:hover {
        border-color: ${({ theme }) => theme.infoTextColor};
    }

    &:active {
        cursor: grabbing;
    }
`;

const OrderBadge = styled.span`
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    flex-shrink: 0;
`;

const StopBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const StopTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 3px;
    min-width: 0;
`;

const SiteName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StopMeta = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const StopEnd = styled.span`
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 3px;
`;
